import { useCallback, useEffect, useMemo, useState } from "react";
import { getPerf, onPerf } from "../lib/perf";
import { useCurrentTheme } from "../lib/theme";
import { LivingSubstrate } from "./webgl/LivingSubstrate";
import { OceanField } from "./webgl/OceanField";

type InkSurface = "today" | "records" | "person" | "default";

interface BordeauxOceanLayerProps {
  surface?: InkSurface;
  density?: number;
  energy?: number;
  hue?: number;
}

function clamp01(value: number | undefined, fallback: number): number {
  if (typeof value !== "number" || Number.isNaN(value)) return fallback;
  return Math.min(1, Math.max(0, value));
}

function shouldUseStaticPoster(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const mobile = window.matchMedia?.("(max-width: 640px)")?.matches ?? false;
  const coarse = window.matchMedia?.("(pointer: coarse)")?.matches ?? false;
  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 8;
  const saveData =
    (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData ?? false;
  return mobile && coarse && memory <= 4 && saveData;
}

function surfaceTuning(surface: InkSurface, density?: number, energy?: number, hue?: number) {
  const d = clamp01(density, surface === "records" ? 0.52 : 0.68);
  const e = clamp01(energy, surface === "records" ? 0.52 : surface === "person" ? 0.62 : 0.74);
  if (surface === "records") {
    return { hue: 0.42, intensity: 0.76 + e * 0.14, amplitude: 0.30 + d * 0.07 };
  }
  if (surface === "today") {
    return { hue: 0.63, intensity: 0.80 + e * 0.16, amplitude: 0.34 + d * 0.08 };
  }
  if (surface === "person") {
    return {
      hue: clamp01(hue, 0.56),
      intensity: 0.78 + e * 0.18,
      amplitude: 0.32 + d * 0.10,
    };
  }
  return { hue: clamp01(hue, 0.55), intensity: 0.84, amplitude: 0.38 };
}

function BordeauxPosterFrame({ surface, hue, intensity }: { surface: InkSurface; hue: number; intensity: number }): JSX.Element {
  const theme = useCurrentTheme();
  const warm = hue >= 0.55;
  const lightBackground = warm
    ? [
        "radial-gradient(ellipse 54% 38% at 68% 18%, rgba(244, 226, 175, 0.34), transparent 58%)",
        "radial-gradient(ellipse 46% 68% at 76% 44%, rgba(176, 29, 55, 0.74), transparent 68%)",
        "radial-gradient(ellipse 70% 34% at 48% 64%, rgba(84, 13, 31, 0.96), transparent 72%)",
        "linear-gradient(180deg, #2A0711 0%, #6D1425 42%, #B62F4A 50%, #21070E 100%)",
      ]
    : [
        "radial-gradient(ellipse 50% 38% at 70% 18%, rgba(224, 204, 151, 0.30), transparent 58%)",
        "radial-gradient(ellipse 54% 72% at 76% 44%, rgba(117, 34, 91, 0.70), transparent 70%)",
        "radial-gradient(ellipse 72% 34% at 46% 64%, rgba(57, 13, 45, 0.98), transparent 72%)",
        "linear-gradient(180deg, #1D0717 0%, #4F1644 42%, #8D284E 51%, #16050C 100%)",
      ];
  const darkBackground = warm
    ? [
        "radial-gradient(ellipse 54% 38% at 68% 18%, rgba(214, 190, 122, 0.22), transparent 58%)",
        "radial-gradient(ellipse 46% 68% at 76% 44%, rgba(160, 44, 68, 0.70), transparent 68%)",
        "radial-gradient(ellipse 70% 34% at 48% 64%, rgba(78, 16, 29, 0.98), transparent 72%)",
        "linear-gradient(180deg, #070305 0%, #4E101D 42%, #8C1F2F 51%, #090307 100%)",
      ]
    : [
        "radial-gradient(ellipse 54% 38% at 68% 18%, rgba(214, 190, 122, 0.18), transparent 58%)",
        "radial-gradient(ellipse 50% 70% at 76% 44%, rgba(88, 31, 102, 0.76), transparent 70%)",
        "radial-gradient(ellipse 70% 34% at 46% 64%, rgba(50, 12, 48, 0.98), transparent 72%)",
        "linear-gradient(180deg, #070306 0%, #33102E 42%, #6F2246 51%, #090307 100%)",
      ];
  return (
    <div
      aria-hidden="true"
      className="bordeaux-poster-frame"
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        background: (theme === "light" ? lightBackground : darkBackground).join(", "),
        filter: `saturate(${surface === "records" ? 1.06 : 1.12}) contrast(${1.05 + intensity * 0.08})`,
      }}
    />
  );
}

export function BordeauxOceanLayer({
  surface = "default",
  density,
  energy,
  hue,
}: BordeauxOceanLayerProps = {}): JSX.Element {
  const theme = useCurrentTheme();
  const [reducedMotion, setReducedMotion] = useState(getPerf().reducedMotion);
  const [ready, setReady] = useState(false);
  const [liveEnabled, setLiveEnabled] = useState(true);
  const [staticOnly, setStaticOnly] = useState(() => getPerf().tier === "low" || shouldUseStaticPoster());
  const tuning = useMemo(() => surfaceTuning(surface, density, energy, hue), [density, energy, hue, surface]);
  const handleReady = useCallback(() => setReady(true), []);
  const handleUnavailable = useCallback(() => setLiveEnabled(false), []);

  useEffect(() => {
    const updateStatic = () => setStaticOnly(getPerf().tier === "low" || shouldUseStaticPoster());
    const unsubscribe = onPerf((perf) => {
      setReducedMotion(perf.reducedMotion);
      setStaticOnly(perf.tier === "low" || shouldUseStaticPoster());
    });
    const mobile = window.matchMedia?.("(max-width: 640px)");
    const coarse = window.matchMedia?.("(pointer: coarse)");
    const connection = (navigator as Navigator & {
      connection?: { addEventListener?: (type: "change", listener: () => void) => void; removeEventListener?: (type: "change", listener: () => void) => void };
    }).connection;
    mobile?.addEventListener?.("change", updateStatic);
    coarse?.addEventListener?.("change", updateStatic);
    connection?.addEventListener?.("change", updateStatic);
    return () => {
      unsubscribe();
      mobile?.removeEventListener?.("change", updateStatic);
      coarse?.removeEventListener?.("change", updateStatic);
      connection?.removeEventListener?.("change", updateStatic);
    };
  }, []);
  useEffect(() => {
    setReady(false);
    setLiveEnabled(!staticOnly);
  }, [theme, surface, density, energy, hue, staticOnly]);
  useEffect(() => {
    if (ready || !liveEnabled || staticOnly) return undefined;
    // The static poster is always visible underneath, so a longer grace period
    // costs the user nothing visually but lets a slow GPU (or a headless
    // SwiftShader capture) finish compiling the raymarch shader and crossfade in
    // the real water instead of permanently falling back to the poster.
    const failClosed = window.setTimeout(() => setLiveEnabled(false), 5000);
    return () => window.clearTimeout(failClosed);
  }, [ready, liveEnabled, staticOnly, theme, surface, density, energy, hue]);

  // Keep the public layer name for existing call sites. The poster is always
  // present and the live ocean crossfades only after a real rendered frame.
  return (
    <div
      aria-hidden="true"
      data-ink-ready={ready ? "true" : "false"}
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden",
        WebkitMaskImage: "radial-gradient(ellipse 96% 88% at 52% 42%, black 0%, black 66%, rgba(0,0,0,0.84) 78%, transparent 100%)",
        maskImage: "radial-gradient(ellipse 96% 88% at 52% 42%, black 0%, black 66%, rgba(0,0,0,0.84) 78%, transparent 100%)",
      }}
    >
      <BordeauxPosterFrame surface={surface} hue={tuning.hue} intensity={tuning.intensity} />
      {liveEnabled && !staticOnly && (
        <div
          aria-hidden="true"
          className="bordeaux-ink-skeleton"
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background:
              "linear-gradient(105deg, rgba(18,5,11,0.70) 0%, rgba(92,16,33,0.58) 36%, rgba(214,190,122,0.20) 50%, rgba(92,16,33,0.58) 64%, rgba(18,5,11,0.70) 100%)",
            backgroundSize: "220% 100%",
          }}
        />
      )}
      {liveEnabled && !staticOnly && (
        <OceanField
          theme={theme}
          hue={tuning.hue}
          intensity={tuning.intensity}
          amplitude={tuning.amplitude}
          reducedMotion={reducedMotion}
          contained
          interactive={surface === "default"}
          targetFps={30}
          onReady={handleReady}
          onUnavailable={handleUnavailable}
          className="bordeaux-live-ink"
        />
      )}
    </div>
  );
}

export function HeroScrim({ strength = "standard" }: { strength?: "strong" | "standard" | "soft" }): JSX.Element {
  const theme = useCurrentTheme();
  const opacity = theme === "light"
    ? (strength === "strong" ? 0.58 : strength === "soft" ? 0.24 : 0.38)
    : (strength === "strong" ? 0.50 : strength === "soft" ? 0.14 : 0.24);
  return (
    <div
      aria-hidden="true"
      className="hero-scrim"
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        opacity,
        background:
          theme === "light"
            ? "linear-gradient(90deg, rgba(60, 11, 22, 0.80) 0%, rgba(80, 14, 28, 0.48) 48%, rgba(80, 14, 28, 0.24) 100%), linear-gradient(180deg, rgba(70, 10, 22, 0.22), rgba(30, 5, 12, 0.52))"
            : "linear-gradient(90deg, rgba(8, 4, 7, 0.86) 0%, rgba(24, 5, 12, 0.54) 52%, rgba(24, 5, 12, 0.28) 100%), linear-gradient(180deg, rgba(10, 3, 7, 0.20), rgba(10, 3, 7, 0.60))",
      }}
    />
  );
}

export function LivingSubstrateLayer(): JSX.Element {
  return <LivingSubstrate />;
}
